import { generatePath } from 'react-router-dom'
import { GridCellProps, GridColumnMenuProps, GridColumnProps } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { PlatformStatisticsDto } from 'api/Trading-Accounts/Types/PlatformStatisticsDto'
import { Link } from 'components/Link'
import { PlatformStatisticsKey } from 'store/transactions/transaction.types'
import { links } from 'utils/featureToggles'

import { ColumnCheckboxFilter } from './ColumnCheckboxFilter'
import { ColumnMenu } from './ColumnMenu'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const clientDetailsStatisticsColumns = ({
  statistics,
}: {
  statistics: PlatformStatisticsDto[]
}): GridColumn[] => [
  {
    title: 'Account',
    field: PlatformStatisticsKey.NAME,
    cell: (props) => {
      if (!props.dataItem.id) {
        return <td>{props.dataItem.name}</td>
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: props.dataItem.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.name}
          </Link>
        </td>
      )
    },
    width: 110,
    show: true,
    className: 'table-columns__content',
  },
  {
    title: 'Type',
    field: PlatformStatisticsKey.ACCOUNT_GROUP,
    columnMenu: (event: GridColumnMenuProps) => (
      <ColumnCheckboxFilter {...event} data={statistics} />
    ),
    cell: (props) => {
      if (!props.dataItem.accountGroup) {
        return <td />
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsAccountGroups.detailsUrl as string, {
                id: props.dataItem.accountGroup.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.accountGroup.name}
          </Link>
        </td>
      )
    },
    width: 120,
    show: true,
    className: 'table-columns__content',
  },
  {
    title: 'Currency',
    field: PlatformStatisticsKey.CURRENCY,
    columnMenu: (event: GridColumnMenuProps) => (
      <ColumnCheckboxFilter {...event} data={statistics} />
    ),
    width: 120,
    show: true,
    className: 'table-columns__content',
  },
  {
    title: 'Platform',
    field: PlatformStatisticsKey.PLATFORM_TYPE,
    columnMenu: (event: GridColumnMenuProps) => (
      <ColumnCheckboxFilter {...event} data={statistics} />
    ),
    width: 120,
    show: true,
    className: 'table-columns__content',
  },
  {
    title: 'Campaign',
    width: 100,
    field: 'campaigns',
    cell: (props: GridCellProps): JSX.Element => {
      const campaigns: NameDto<string>[] = props.dataItem.campaigns || []
      return <td className='table-columns__content'>{campaigns.map((x) => x.name).join(', ')}</td>
    },
    show: true,
    className: 'table-columns__number',
  },
  {
    title: 'Status',
    field: PlatformStatisticsKey.STATUS,
    columnMenu: (event: GridColumnMenuProps) => (
      <ColumnCheckboxFilter {...event} data={statistics} />
    ),
    width: 100,
    show: true,
    className: 'table-columns__content',
  },
  {
    title: 'Balance',
    field: PlatformStatisticsKey.BALANCE,
    columnMenu: ColumnMenu,
    filter: 'numeric',
    format: '{0:#,##0.00}',
    width: 100,
    show: true,
    className: 'k-font-weight-bold table-columns__number',
  },
  {
    title: 'Equity',
    field: PlatformStatisticsKey.EQUITY,
    columnMenu: ColumnMenu,
    filter: 'numeric',
    format: '{0:#,##0.00}',
    width: 100,
    show: true,
    className: 'table-columns__number',
  },
  {
    title: 'Commissions',
    field: PlatformStatisticsKey.TOTAL_COMMISSIONS,
    columnMenu: ColumnMenu,
    filter: 'numeric',
    format: '{0:#,##0.00}',
    width: 100,
    show: true,
    className: 'table-columns__number',
  },
  {
    title: 'Swaps',
    field: PlatformStatisticsKey.TOTAL_SWAPS,
    columnMenu: ColumnMenu,
    filter: 'numeric',
    format: '{0:#,##0.00}',
    width: 100,
    show: true,
    className: 'table-columns__number',
  },
  {
    title: 'Margin',
    field: 'margin',
    children: [
      {
        title: 'Used',
        field: PlatformStatisticsKey.USED_MARGIN,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Free',
        field: PlatformStatisticsKey.FREE_MARGIN,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Level',
        field: PlatformStatisticsKey.MARGIN_LEVEL,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
    ],
    show: true,
  },
  {
    title: 'Deposits',
    field: 'deposits',
    children: [
      {
        title: 'Number',
        field: PlatformStatisticsKey.TOTAL_NUMBER_OF_DEPOSITS,
        columnMenu: ColumnMenu,
        width: 100,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        className: 'table-columns__number',
      },
      {
        title: 'Amount',
        field: PlatformStatisticsKey.TOTAL_DEPOSIT_AMOUNT,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'First',
        field: PlatformStatisticsKey.FIRST_DEPOSIT,
        columnMenu: ColumnMenu,
        filter: 'date',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
      {
        title: 'Last',
        field: PlatformStatisticsKey.LAST_DEPOSIT,
        columnMenu: ColumnMenu,
        filter: 'date',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
    ],
    show: true,
  },
  {
    title: 'Withdrawals',
    field: 'withdrawals',
    children: [
      {
        title: 'Number',
        field: PlatformStatisticsKey.TOTAL_NUMBER_OF_WITHDRAWALS,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Amount',
        field: PlatformStatisticsKey.TOTAL_WITHDRAWAL_AMOUNT,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'First',
        field: PlatformStatisticsKey.FIRST_WITHDRAWAL,
        columnMenu: ColumnMenu,
        filter: 'date',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
      {
        title: 'Last',
        field: PlatformStatisticsKey.LAST_WITHDRAWAL,
        columnMenu: ColumnMenu,
        filter: 'date',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
    ],
    show: true,
  },
  {
    title: 'Closed Trades',
    field: 'closedTrades',
    children: [
      {
        title: 'Number',
        field: PlatformStatisticsKey.TOTAL_NUMBER_OF_CLOSED_TRADES,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Lots',
        field: PlatformStatisticsKey.TOTAL_CLOSED_LOTS,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Profit',
        field: PlatformStatisticsKey.TOTAL_CLOSED_PROFIT,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
    ],
    show: true,
  },
  {
    title: 'Open Trades',
    field: 'openTrades',
    children: [
      {
        title: 'Number',
        field: PlatformStatisticsKey.TOTAL_NUMBER_OF_OPEN_TRADES,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Lots',
        field: PlatformStatisticsKey.TOTAL_OPEN_LOTS,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
      {
        title: 'Profit',
        field: PlatformStatisticsKey.TOTAL_OPEN_PROFIT,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:#,##0.00}',
        width: 100,
        className: 'table-columns__number',
      },
    ],
    show: true,
  },
  {
    title: 'Trade Dates',
    field: 'tradeDates',
    children: [
      {
        title: 'First',
        field: PlatformStatisticsKey.FIRST_TRADE,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
      {
        title: 'Last',
        field: PlatformStatisticsKey.LAST_TRADE,
        columnMenu: ColumnMenu,
        filter: 'numeric',
        format: '{0:d}',
        width: 125,
        className: 'table-columns__date',
      },
    ],
    show: true,
  },
]
